<template>
  <el-skeleton :loading="skeletonLoadingState" animated>
    <template #template>
      <el-skeleton-item />
    </template>
    <template #default>
      <div class="tw:group stats-section token-stats">
        <div class="tw:group-hover:opacity-100 tw:opacity-0 stats-section-gradient" />

        <div class="stats-section-title">{{ $t('appStatisticOwnedToken') }}</div>

        <!-- Owned primary token  -->
        <div class="stats-section-token">
          <DashboardTokenInfoBlock :key="`owned-${currentToken}`" :main-token-value="ownedPrimaryTokenInjected" />

          <button
            v-if="currentTokenInfoDataInjected?.isSellable"
            class="btn-outline"
            @click="$emit('openPurchaseTokens')"
          >
            <span> {{ $t('dashboardBuyPearls') }} </span>
          </button>
        </div>
        <!-- Owned primary token end -->

        <!-- Button cash -->
        <button
          v-if="store.currentToken === Tokens.pearl"
          class="btn-primary cash-btn"
          @click="$emit('openExchangePopup')"
        >
          <span> {{ $t('appStatisticCash') }} </span>
        </button>
        <!-- Button cash end -->

        <!-- Owned secondary token  -->
        <div
          v-if="secondaryTokenMetadata.name.contract !== currentTokenInfoDataInjected?.key"
          class="stats-section-token"
        >
          <DashboardTokenInfoBlock
            v-if="blackPearlBalanceInjected"
            :key="`owned-secondary-${currentToken}`"
            :extra-token-value="BigNumber(blackPearlBalanceInjected).toFormat(2)"
          />

          <button
            v-if="currentTokenInfoDataInjected?.isSellable"
            class="btn-outline"
            @click="$emit('openPurchaseSecondaryTokens')"
          >
            <span> {{ $t('dashboardBuyPearls') }} </span>
          </button>
        </div>

        <div v-if="currentTokenInfoDataInjected?.isSellable" class="stats-section-title">
          {{
            store.currentToken === Tokens.pearl ? $t('historyModalEventTypeLocked') : $t('appStatisticTotalProduced')
          }}
        </div>

        <!-- Owned secondary token end -->

        <!-- Total produced -->
        <DashboardTokenInfoBlock
          v-if="currentTokenInfoDataInjected?.isSellable && currentToken === Tokens.pearl"
          :key="`locked-${currentToken}`"
          :main-token-value="lockedPrimaryTokenInjected"
          :extra-token-value="lockedSecondaryTokenInjected"
          columned
        />

        <DashboardTokenInfoBlock
          v-else
          :key="`produced-${currentToken}`"
          :main-token-value="primaryTokenTotalProducedInjected"
          :extra-token-value="secondaryTokenTotalProducedInjected"
          columned
        />
        <!-- Total produced end -->
      </div>
    </template>
  </el-skeleton>
</template>

<script setup lang="ts">
import { BigNumber } from 'bignumber.js';
import { Tokens } from '~/utils/constants';
import type { ITokenModel } from '~/types/apiService';

const store = useMainStore();
const { currentToken } = storeToRefs(store);
const { blockchain } = useEnvs();
const { isLoading: isLoadingTotalMinedAmount } = useTotalMined();

defineEmits(['openExchangePopup', 'openPurchaseTokens', 'openPurchaseSecondaryTokens']);

const isLoadingMinedBalanceInjected = inject<Ref<boolean>>('isLoadingMinedBalance');
const isLoadingUpdateDataDashboardInjected = inject<Ref<boolean>>('isLoadingUpdateDataDashboard');
const isLoadingUpdateDataStatisticsInjected = inject<Ref<boolean>>('isLoadingUpdateDataStatistics');
const blackPearlBalanceInjected = inject<Ref<string>>('blackPearlBalance');
const currentTokenInfoDataInjected = inject<Ref<ITokenModel>>('currentTokenInfoData');
const ownedPrimaryTokenInjected = inject<Ref<string>>('ownedPrimaryToken');
const lockedPrimaryTokenInjected = inject<Ref<string>>('lockedPrimaryToken');
const lockedSecondaryTokenInjected = inject<Ref<string>>('lockedSecondaryToken');
const primaryTokenTotalProducedInjected = inject<Ref<string>>('primaryTokenTotalProduced');
const secondaryTokenTotalProducedInjected = inject<Ref<string>>('secondaryTokenTotalProduced');

const skeletonLoadingState = computed(() => {
  const hasNoOwnedData = !ownedPrimaryTokenInjected?.value || ownedPrimaryTokenInjected.value === '';
  const hasNoProducedData = !primaryTokenTotalProducedInjected?.value || primaryTokenTotalProducedInjected.value === '';

  const hasNoData = hasNoOwnedData && hasNoProducedData;
  const isAnyLoading =
    isLoadingMinedBalanceInjected?.value ||
    isLoadingUpdateDataDashboardInjected?.value ||
    isLoadingUpdateDataStatisticsInjected?.value ||
    (isLoadingTotalMinedAmount?.value && currentToken.value !== Tokens.pearl);

  return hasNoData && isAnyLoading;
});

const secondaryTokenMetadata = computed(() => {
  return tokenMetaData(
    blockchain.contracts,
    blockchain.contracts[currentTokenInfoDataInjected?.value?.key as Tokens]?.addresses.secondaryToken as string
  );
});
</script>

<style scoped lang="scss">
.stats-section-gradient {
  z-index: 0;
  background-image: radial-gradient(circle at 100% 100%, rgba(140, 253, 159, 0.2), transparent 50%);
  transition: opacity 0.3s;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
}

.stats-section-token {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex-wrap: wrap;
  margin-bottom: 10px;

  @media screen and (max-width: 560px) {
    flex-wrap: nowrap;
    gap: 10px;

    .btn-outline {
      width: fit-content;
    }
  }
}

.stats-section {
  background-image: radial-gradient(circle at 100% 100%, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0) 50%);
  border: 2px solid #133442;
  border-radius: 12px;
  flex-direction: column;
  flex: 1;
  align-items: flex-start;
  padding: 12px 16px 16px;
  font-size: 24px;
  transition: all 0.3s;
  display: flex;
  position: relative;
  overflow: hidden;
  min-height: 370px;

  @media screen and (max-width: 991px) {
    font-size: 16px;
  }

  @media screen and (max-width: 560px) {
    width: 100%;
    flex: none;
    align-items: center;
    min-height: unset;
  }
}

.stats-section-title {
  font-family: Grenze;
  font-weight: 400;
  font-size: 24px;
  line-height: 1;

  @media screen and (max-width: 991px) {
    font-size: 16px;
  }
}

.stats-section.token-stats {
  .cash-btn {
    margin-bottom: 10px;
    min-width: 210px;

    @media screen and (max-width: 991px) {
      min-width: 100%;
    }
  }

  .custom-btn-claim {
    margin: 0.5rem 0rem;
  }

  @media screen and (min-width: 991px) {
    .custom-btn-claim {
      width: 40%;
    }
  }

  @media screen and (max-width: 560px) {
    display: flex;
    gap: 10px;
    justify-content: space-evenly;
  }
}

.btn-outline {
  min-width: 100px;
}
</style>
